// The bar's breadcrumb. Full when it fits; otherwise the middle goes into
// a "..." menu and the root stays, and when even that is too wide the root
// goes in after it. The nearest step back and the page are always drawn,
// so the way up is always one click away.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDismissable } from "../../hooks/useDismissable";
import { Skeleton } from "./Skeleton";
import type { TrailStep } from "./trail";

// Below three steps there is nothing but the parent and the page.
const FOLDABLE = 3;

function Step({ step }: { step: TrailStep }) {
  if (step.pending) {
    return (
      <span className="cc-trail-step" aria-busy="true">
        <Skeleton width={90} height={12} />
      </span>
    );
  }
  return step.to ? (
    <Link className="cc-trail-step" to={step.to} title={step.label}>
      {step.label}
    </Link>
  ) : (
    <span className="cc-trail-step" title={step.label}>
      {step.label}
    </span>
  );
}

function Current({ step }: { step: TrailStep }) {
  if (step.pending) {
    return (
      <span className="cc-trail-current" aria-busy="true">
        <Skeleton width={90} height={12} />
      </span>
    );
  }
  return (
    <h1 className="cc-trail-current" aria-current="page" title={step.label}>
      {step.label}
    </h1>
  );
}

// A step at its natural width, for the fits-or-folds measurement.
function GhostStep({ step, current }: { step: TrailStep; current?: boolean }) {
  return (
    <li>
      <span className={current ? "cc-trail-current" : "cc-trail-step"}>
        {step.pending ? <Skeleton width={90} height={12} /> : step.label}
      </span>
    </li>
  );
}

export function TrailNav({ steps }: { steps: TrailStep[] }) {
  const slotRef = useRef<HTMLElement>(null);
  const fullRef = useRef<HTMLOListElement>(null);
  const keptRef = useRef<HTMLOListElement>(null);
  // 0: the whole trail; 1: the middle folded, the root kept; 2: the root
  // folded too.
  const [fold, setFold] = useState(0);
  const { open, setOpen, containerRef } = useDismissable<HTMLLIElement>();
  const foldable = steps.length >= FOLDABLE;
  // Keeping the root is a shape of its own only when there is a middle.
  const keepable = steps.length > FOLDABLE;

  // The slot's width ignores the trail (see .cc-topbar-lead), so this never
  // feeds back into itself.
  // Fractional widths: rounded ones can pick a shape that overflows by a
  // fraction of a pixel, which is enough for an ellipsis.
  const measure = useCallback(() => {
    const slot = slotRef.current;
    const full = fullRef.current;
    if (!slot || !full) return;
    const width = (el: Element) => el.getBoundingClientRect().width;
    const room = width(slot);
    const kept = keptRef.current;
    setFold(width(full) <= room ? 0 : kept && width(kept) <= room ? 1 : 2);
  }, []);

  useLayoutEffect(() => {
    if (!foldable || !slotRef.current || !fullRef.current) return;
    measure();
    // The slot moves with the window and the pill, the ghosts with a font swap.
    const ro = new ResizeObserver(measure);
    ro.observe(slotRef.current);
    ro.observe(fullRef.current);
    if (keptRef.current) ro.observe(keptRef.current);
    return () => ro.disconnect();
  }, [foldable, steps, measure]);

  const level = foldable ? fold : 0;
  // The "..." leaves with its menu's state still set: close it, or the
  // menu comes back open with the next fold.
  useEffect(() => {
    if (level === 0) setOpen(false);
  }, [level, setOpen]);
  const last = steps.length - 1;
  const hidden = level === 0 ? [] : steps.slice(level === 2 ? 0 : 1, last - 1);
  const shown = level === 0 ? steps.slice(1) : [steps[last - 1], steps[last]];
  const names = hidden.filter((s) => !s.pending).map((s) => s.label);
  const hint = names.length ? names.join(", ") : "Loading";

  return (
    <nav className="cc-trail" aria-label="Breadcrumb" ref={slotRef}>
      <ol>
        {level < 2 && (
          <li>{last === 0 ? <Current step={steps[0]} /> : <Step step={steps[0]} />}</li>
        )}
        {level > 0 && (
          <li className="cc-trail-more" ref={containerRef}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={"Show hidden steps: " + hint}
              title={hint}
              disabled={names.length === 0}
              onClick={() => setOpen((v) => !v)}
            >
              ...
            </button>
            {open && (
              <div className="cc-trail-menu cc-card" role="menu" aria-label="Hidden steps">
                {hidden.map((s, i) =>
                  s.pending ? null : s.to ? (
                    <Link key={i} role="menuitem" to={s.to}>
                      {s.label}
                    </Link>
                  ) : (
                    <span key={i} role="menuitem" aria-disabled="true">
                      {s.label}
                    </span>
                  ),
                )}
              </div>
            )}
          </li>
        )}
        {shown.map((s, i) => (
          <li key={i}>{i === shown.length - 1 ? <Current step={s} /> : <Step step={s} />}</li>
        ))}
      </ol>
      {foldable && (
        <ol className="cc-trail-ghost" aria-hidden="true" ref={fullRef}>
          {steps.map((s, i) => (
            <GhostStep key={i} step={s} current={i === last} />
          ))}
        </ol>
      )}
      {keepable && (
        <ol className="cc-trail-ghost cc-trail-ghost-kept" aria-hidden="true" ref={keptRef}>
          <GhostStep step={steps[0]} />
          <li className="cc-trail-more">
            <button type="button" disabled>
              ...
            </button>
          </li>
          <GhostStep step={steps[last - 1]} />
          <GhostStep step={steps[last]} current />
        </ol>
      )}
    </nav>
  );
}
